
import React, { useState, useMemo } from 'react';
import type { Suggestion, User, SuggestionStatus, Language, SuggestionArea } from '../types';
import XIcon from './icons/XIcon';
import SparklesIcon from './icons/SparklesIcon';
import LayoutGridIcon from './icons/LayoutGridIcon';
import ListIcon from './icons/ListIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import CalendarIcon from './icons/CalendarIcon';
import { getTranslation } from '../utils/translations';

interface SuggestionManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    suggestions: Suggestion[];
    onUpdateStatus: (id: string, status: SuggestionStatus) => Promise<void>;
}

type DateRange = 'all' | 'today' | 'week' | 'month';

const STATUS_COLORS: Record<string, string> = {
    new: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    denied: 'bg-red-100 text-red-700',
    archive: 'bg-slate-100 text-slate-500',
    fulfilled: 'bg-amber-100 text-amber-700'
};

// Extracted Sub-component for better performance and error isolation
const SuggestionItem: React.FC<{
    suggestion: Suggestion;
    viewMode: 'grid' | 'list';
    onUpdateStatus: (id: string, status: SuggestionStatus) => Promise<void>;
    t: (key: string) => string;
}> = ({ suggestion, viewMode, onUpdateStatus, t }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    const dateStr = new Date(suggestion.date).toLocaleDateString();
    const displayName = (suggestion.reporterEmail || 'Anonymous').split('@')[0];
    const status = suggestion.status || 'new';

    const getStatusColor = (s: string) => STATUS_COLORS[s] || STATUS_COLORS['new'];
    
    const getDotColorClass = (s: string) => {
        const colorClass = getStatusColor(s);
        const parts = colorClass.split(' ');
        if (parts.length > 1) {
            return parts[1].replace('text-', 'bg-');
        }
        return 'bg-slate-400';
    };

    if (viewMode === 'list') {
        return (
            <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden hover:border-amber-200 transition-colors">
                <div 
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="p-4 flex items-center justify-between cursor-pointer group"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getDotColorClass(status)}`} />
                        <div className="min-w-0">
                            <p className="font-bold text-slate-800 truncate text-sm">{suggestion.subject}</p>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">{t(suggestion.area)} • {displayName}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-[9px] font-bold text-slate-300">{dateStr}</span>
                        <div className="text-slate-300 group-hover:text-slate-600 transition-colors">
                            {isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                        </div>
                    </div>
                </div>
                {isExpanded && (
                    <div className="px-4 pb-4 pt-0 animate-fade-in space-y-4">
                        <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100 italic">
                            "{suggestion.text}"
                        </p>
                        <div className="flex justify-between items-center gap-2">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] font-black text-slate-400 uppercase">{t('status')}:</span>
                                <select 
                                    value={status} 
                                    onChange={e => onUpdateStatus(suggestion.id, e.target.value as SuggestionStatus)}
                                    className={`text-[9px] font-black uppercase px-2 py-1 rounded border-none outline-none focus:ring-2 ring-amber-500 cursor-pointer transition-colors ${getStatusColor(status)}`}
                                >
                                    {Object.keys(STATUS_COLORS).map(st => <option key={st} value={st}>{t(st)}</option>)}
                                </select>
                            </div>
                            <span className="text-[9px] font-bold text-slate-400 dir-ltr">{suggestion.reporterEmail}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="bg-white border border-slate-200 rounded-[2rem] p-6 shadow-sm flex flex-col gap-4 relative hover:border-amber-300 transition-all group">
            <div className="flex justify-between items-start cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="min-w-0 pr-4">
                    <h4 className="font-black text-slate-800 tracking-tight leading-tight mb-1">{suggestion.subject}</h4>
                    <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black uppercase text-slate-400 tracking-widest">{t(suggestion.area)}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                        <span className="text-[9px] font-bold text-slate-400 dir-ltr">{displayName}</span>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <select 
                        value={status} 
                        onClick={e => e.stopPropagation()}
                        onChange={e => onUpdateStatus(suggestion.id, e.target.value as SuggestionStatus)}
                        className={`text-[9px] font-black uppercase px-2 py-1 rounded-full border-none outline-none focus:ring-2 ring-amber-500 cursor-pointer transition-colors shadow-sm ${getStatusColor(status)}`}
                    >
                        {Object.keys(STATUS_COLORS).map(st => <option key={st} value={st}>{t(st)}</option>)}
                    </select>
                    <div className="text-slate-200 group-hover:text-slate-400 transition-colors">
                        {isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="animate-fade-in space-y-4">
                    <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 min-h-[4rem]">
                        {suggestion.text}
                    </p>
                    <div className="flex justify-between items-center border-t border-slate-50 pt-4">
                        <div className="flex flex-col">
                            <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-0.5">{t('submittedOn')}</span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase">{dateStr}</span>
                        </div>
                        <span className="text-[9px] font-bold text-slate-300 dir-ltr">{suggestion.reporterEmail}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const SuggestionManagementModal: React.FC<SuggestionManagementModalProps> = ({ isOpen, onClose, user, suggestions, onUpdateStatus }) => {
    const t = (key: any) => getTranslation(user?.language || 'en', key);
    
    // Default to 'new' as requested
    const [statusFilter, setStatusFilter] = useState<SuggestionStatus | 'all'>('new');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [dateRange, setDateRange] = useState<DateRange>('all');

    if (!isOpen) return null;

    const filteredSuggestions = (suggestions || []).filter(s => {
        // 1. Status Filter
        if (statusFilter !== 'all' && s.status !== statusFilter) return false;

        // 2. Date Filter
        if (dateRange !== 'all') {
            const now = new Date();
            const cutoff = new Date();
            const sDate = new Date(s.date);
            
            if (dateRange === 'today') {
                cutoff.setHours(0, 0, 0, 0);
                if (sDate < cutoff) return false;
            } else if (dateRange === 'week') {
                cutoff.setDate(now.getDate() - 7);
                if (sDate < cutoff) return false;
            } else if (dateRange === 'month') {
                cutoff.setMonth(now.getMonth() - 1);
                if (sDate < cutoff) return false;
            }
        }
        return true;
    });

    const filterButtonBase = "px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-full border transition-all shadow-sm flex-shrink-0";

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4" onClick={onClose}>
            <div className="bg-slate-50 rounded-[2.5rem] shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden border border-white/20" onClick={e => e.stopPropagation()}>
                {/* Header Section */}
                <div className="p-6 border-b bg-white flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="bg-amber-100 p-3 rounded-[1.25rem] text-amber-600 shadow-sm">
                            <SparklesIcon className="h-7 w-7" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-800 tracking-tight leading-none mb-1">{t('manageSuggestions')}</h2>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{t('totalRequests').replace('{count}', suggestions.length.toString())}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* View Mode Toggle */}
                        <div className="flex bg-slate-100 p-1 rounded-xl gap-0.5 border border-slate-200 shadow-inner">
                            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-400 hover:text-slate-600'}`}><ListIcon className="w-4 h-4" /></button>
                            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-400 hover:text-slate-600'}`}><LayoutGridIcon className="w-4 h-4" /></button>
                        </div>
                        <button onClick={onClose} className="p-2 text-slate-300 hover:text-slate-800 transition-colors">&times;</button>
                    </div>
                </div>

                {/* Filter Controls Sticky Section */}
                <div className="p-5 bg-white border-b border-slate-100 space-y-4 shadow-sm shrink-0">
                    {/* Status Filters */}
                    <div className="flex items-center gap-4 overflow-x-auto scrollbar-none">
                        <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] shrink-0">{t('status')}:</span>
                        <div className="flex gap-2">
                            <button 
                                onClick={() => setStatusFilter('all')}
                                className={`${filterButtonBase} ${statusFilter === 'all' ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                            >
                                {t('allStatuses')}
                            </button>
                            {(['new', 'approved', 'denied', 'fulfilled', 'archive'] as const).map(st => (
                                <button 
                                    key={st}
                                    onClick={() => setStatusFilter(st)}
                                    className={`${filterButtonBase} ${statusFilter === st ? 'bg-amber-500 border-amber-500 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                                >
                                    {t(st)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date Presets */}
                    <div className="flex items-center gap-4 overflow-x-auto scrollbar-none pt-2 border-t border-slate-50">
                        <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] shrink-0">{t('timeline')}:</span>
                        <div className="flex gap-1.5">
                            {(['all', 'today', 'week', 'month'] as const).map(range => (
                                <button 
                                    key={range}
                                    onClick={() => setDateRange(range)}
                                    className={`
                                        flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all
                                        ${dateRange === range ? 'bg-sky-50 text-sky-700 border border-sky-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}
                                    `}
                                >
                                    <CalendarIcon className="w-3.5 h-3.5" />
                                    {range === 'all' ? t('allTime') : range === 'today' ? t('today') : range === 'week' ? t('last7Days') : t('last30Days')}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="p-6 overflow-y-auto flex-grow bg-slate-50/50 scrollbar-none">
                    {filteredSuggestions.length === 0 ? (
                        <div className="text-center py-32 bg-white rounded-[3rem] border border-dashed border-slate-200 flex flex-col items-center gap-4 animate-fade-in">
                            <div className="p-6 bg-slate-50 rounded-full text-slate-200">
                                <SparklesIcon className="w-16 h-16" />
                            </div>
                            <p className="text-slate-400 font-black uppercase tracking-[0.2em] text-xs italic">{t('noResults')}</p>
                        </div>
                    ) : (
                        <div className={viewMode === 'grid' ? "grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in" : "flex flex-col gap-3 animate-fade-in"}>
                            {filteredSuggestions.map(s => (
                                <SuggestionItem 
                                    key={s.id} 
                                    suggestion={s} 
                                    viewMode={viewMode} 
                                    onUpdateStatus={onUpdateStatus}
                                    t={t}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 bg-white border-t flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-2">
                         <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('showingItems').replace('{count}', filteredSuggestions.length.toString())}</span>
                    </div>
                    <button onClick={onClose} className="px-12 py-3 bg-slate-900 text-white font-black rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 active:scale-95 uppercase tracking-widest text-xs">
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SuggestionManagementModal;
